interface NavigationItem {
  id: string;
  title: string;
  level: number;
  children?: NavigationItem[];
  filename?: string;
}

interface FlatSection {
  id: string;
  title: string;
  filename?: string;
}

/**
 * Flattens the hierarchical navigation structure into a linear array
 * of sections in reading order
 */
export function flattenNavigation(navigation: NavigationItem[]): FlatSection[] {
  const flattened: FlatSection[] = [];
  
  function traverse(items: NavigationItem[]) {
    for (const item of items) {
      // Add the item if it has a filename (is a readable section)
      if (item.filename) {
        flattened.push({
          id: item.id,
          title: item.title,
          filename: item.filename
        });
      }
      
      // Recursively traverse children
      if (item.children && item.children.length > 0) {
        traverse(item.children);
      }
    }
  }
  
  traverse(navigation);
  return flattened;
}

/**
 * Gets the previous section in reading order
 */
export function getPreviousSection(
  currentSectionId: string, 
  navigation: NavigationItem[]
): FlatSection | null {
  const flatSections = flattenNavigation(navigation);
  const currentIndex = flatSections.findIndex(section => section.id === currentSectionId);
  
  if (currentIndex <= 0) {
    return null; // No previous section
  }
  
  return flatSections[currentIndex - 1];
}

/**
 * Gets the next section in reading order
 */
export function getNextSection(
  currentSectionId: string, 
  navigation: NavigationItem[]
): FlatSection | null {
  const flatSections = flattenNavigation(navigation);
  const currentIndex = flatSections.findIndex(section => section.id === currentSectionId);
  
  if (currentIndex === -1 || currentIndex >= flatSections.length - 1) {
    return null; // No next section
  }
  
  return flatSections[currentIndex + 1];
}

/**
 * Gets the current section details
 */
export function getCurrentSection(
  currentSectionId: string, 
  navigation: NavigationItem[]
): FlatSection | null {
  const flatSections = flattenNavigation(navigation);
  return flatSections.find(section => section.id === currentSectionId) || null;
}

/**
 * Gets navigation context for a section (previous, current, next)
 */
export function getNavigationContext(
  currentSectionId: string, 
  navigation: NavigationItem[]
) {
  return {
    previous: getPreviousSection(currentSectionId, navigation),
    current: getCurrentSection(currentSectionId, navigation),
    next: getNextSection(currentSectionId, navigation)
  };
}

/**
 * Gets the first readable section in the book
 */
export function getFirstSection(navigation: NavigationItem[]): FlatSection | null {
  const flatSections = flattenNavigation(navigation);
  return flatSections.length > 0 ? flatSections[0] : null;
}

/**
 * Gets the last readable section in the book
 */
export function getLastSection(navigation: NavigationItem[]): FlatSection | null {
  const flatSections = flattenNavigation(navigation);
  return flatSections.length > 0 ? flatSections[flatSections.length - 1] : null;
}

/**
 * Checks if a section exists in the navigation
 */
export function sectionExists(
  sectionId: string, 
  navigation: NavigationItem[]
): boolean {
  return getCurrentSection(sectionId, navigation) !== null;
}

/**
 * Gets the total number of readable sections
 */
export function getTotalSections(navigation: NavigationItem[]): number {
  return flattenNavigation(navigation).length;
}

/**
 * Gets the position of a section in the reading order (1-based)
 */
export function getSectionPosition(
  currentSectionId: string, 
  navigation: NavigationItem[]
): number {
  const flatSections = flattenNavigation(navigation);
  const index = flatSections.findIndex(section => section.id === currentSectionId);
  return index === -1 ? 0 : index + 1;
}

/**
 * Scrolls to the top of the content area smoothly
 */
export function scrollToTop(smooth: boolean = true): void {
  const scrollOptions: ScrollToOptions = {
    top: 0,
    left: 0,
    behavior: smooth ? 'smooth' : 'auto'
  };
  
  // Try multiple selectors to find the main scrollable container
  const selectors = [
    '.content-area',
    '.book-content', 
    'main',
    '.card:has(.card-title)', // Target the main content card
    '[data-scroll-container]' // Custom data attribute for future use
  ];
  
  for (const selector of selectors) {
    const element = document.querySelector(selector);
    if (element && element.scrollHeight > element.clientHeight) {
      element.scrollTo(scrollOptions);
      break;
    }
  }
  
  // Always scroll the window as well to ensure we're at the top
  window.scrollTo(scrollOptions);
  
  // Additional scroll to ensure we reach the very top
  if (smooth) {
    // Use requestAnimationFrame to ensure smooth scrolling completes
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  }
}

/**
 * Enhanced scroll to top with content loading coordination
 */
export function scrollToTopWithDelay(delay: number = 150): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      scrollToTop(true);
      // Wait a bit more to ensure scroll completes
      setTimeout(resolve, 100);
    }, delay);
  });
}
